import React, { useState, useEffect, useCallback } from 'react';
import { rewardService } from '../api/services';
import RewardItemModal from './RewardItemModal';
import RewardReviewList from './RewardReviewList';
import RewardRedemptionRecords from './RewardRedemptionRecords';
import { statusLabel } from '../utils/adminLabels';
import './Reward.css';

// 後台「點數兌換」分頁：商品管理（CRUD＋每商品兌換明細）與兌換審核佇列。
const AdminRewardPanel = () => {
  const [subTab, setSubTab] = useState('items');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingItem, setEditingItem] = useState(null); // 物件或 'new'
  const [recordsItem, setRecordsItem] = useState(null);
  // 待審核兌換筆數，供「兌換審核」子分頁計數提示
  const [reviewCount, setReviewCount] = useState(0);

  const loadItems = useCallback(() => {
    setLoading(true);
    return rewardService.list(true)
      .then((res) => setItems(res.data?.items || []))
      .catch((err) => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, []);

  // 載入待審核筆數（PENDING）供分頁計數
  const loadReviewCount = useCallback(() => rewardService.reviewQueue('PENDING')
    .then((res) => setReviewCount(res.data?.items?.length || 0))
    .catch(() => {}), []);

  // 進入面板即載入商品與待審計數，讓兩個子分頁的計數一開始就正確
  useEffect(() => { loadItems(); loadReviewCount(); }, [loadItems, loadReviewCount]);

  const handleDelete = async (id) => {
    if (!window.confirm('確定下架此商品？已產生的兌換紀錄仍會保留。')) return;
    try {
      await rewardService.remove(id);
      await loadItems();
    } catch (err) { setError(err.response?.data?.error || err.message); }
  };

  return (
    <div className="admin-reward-panel">
      <div className="reward-subtabs">
        <button className={`btn btn-sm ${subTab === 'items' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setSubTab('items')}>
          商品管理{items.length > 0 && <span className="reward-tab-count">{items.length}</span>}
        </button>
        <button className={`btn btn-sm ${subTab === 'review' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setSubTab('review')}>
          兌換審核{reviewCount > 0 && <span className="reward-tab-count is-alert">{reviewCount}</span>}
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {subTab === 'items' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ margin: 0 }}>兌換商品</h2>
            <button className="btn btn-sm btn-primary" onClick={() => setEditingItem('new')}>＋ 上架商品</button>
          </div>
          {loading ? (
            <div className="reward-empty">載入中...</div>
          ) : items.length === 0 ? (
            <div className="reward-empty">尚未上架任何兌換商品</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table">
                <thead>
                  <tr><th>圖片</th><th>名稱</th><th>所需點數</th><th>庫存</th><th>狀態</th><th>操作</th></tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id}>
                      <td>{it.image_url ? <img src={it.image_url} alt={it.name} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }} /> : '—'}</td>
                      <td>{it.name}</td>
                      <td>{it.points_cost}</td>
                      <td>{it.stock === null || it.stock === undefined ? '無限' : it.stock}</td>
                      <td><span className={`badge badge-${it.status === 'ACTIVE' ? 'success' : 'danger'}`}>{statusLabel(it.status)}</span></td>
                      <td>
                        <div className="btn-group">
                          <button className="btn btn-sm btn-secondary" onClick={() => setEditingItem(it)}>編輯</button>
                          <button className="btn btn-sm btn-secondary" onClick={() => setRecordsItem(it)}>兌換明細</button>
                          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(it.id)}>下架</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {subTab === 'review' && <RewardReviewList onReviewed={loadReviewCount} />}

      {editingItem && (
        <RewardItemModal
          item={editingItem === 'new' ? null : editingItem}
          onClose={() => setEditingItem(null)}
          onSaved={loadItems}
        />
      )}
      {recordsItem && <RewardRedemptionRecords item={recordsItem} onClose={() => setRecordsItem(null)} />}
    </div>
  );
};

export default AdminRewardPanel;
